import React from 'react';
import { cn } from '@/utils';
import { AppImage, SkeletonLoader } from '@/components';
import { roleplayCardIntroStyles } from '../RoleplayCard.styles';

export interface RoleplayCardIntroProps extends React.HTMLAttributes<HTMLDivElement> {
  /**
   * The avatar's name for the roleplay scenario.
   */
  avatarName?: string;

  /**
   * The avatar image source for the doctor.
   */
  avatarSrc?: string;

  /**
   * Alternative text for the avatar image.
   */
  avatarAlt?: string;

  /**
   * The description text explaining the roleplay scenario.
   */
  description: string;

  /**
   * EM Code associated with the roleplay scenario.
   */
  emCode?: string;

  /**
   * Loading state to indicate whether the content is still being fetched or processed.
   */
  isLoading?: boolean;
}

/**
 * RoleplayCardIntro displays the introduction section of a roleplay card
 * with a description and doctor avatar information.
 */
export const RoleplayCardIntro: React.FC<RoleplayCardIntroProps> = ({
  avatarAlt,
  avatarName,
  avatarSrc,
  className,
  description,
  emCode,
  isLoading = false,
  ...props
}) => {
  /**
   * Combine the base styles from roleplayCardIntroVariants with any additional
   * className passed as a prop to allow for customization while maintaining the
   * default styling of the component.
   */
  const containerClassNames = cn(roleplayCardIntroStyles.base, className);

  /**
   * Determine the appropriate class names for the description text. If the description
   * includes newline characters, apply the 'whitespace-pre-line' class to preserve
   * the formatting of the text. This ensures that line breaks in the description
   * are rendered correctly.
   */
  const descriptionClassNames = cn(roleplayCardIntroStyles.description, {
    'whitespace-pre-line': description?.includes('\n'),
  });

  // ========================================================================
  // Render the component with conditional loading states for the
  // description and avatar information.
  // ========================================================================
  return (
    <div className={containerClassNames} {...props}>
      <div className={roleplayCardIntroStyles.content}>
        <div className={roleplayCardIntroStyles.descriptionContainer}>
          {isLoading ? (
            <SkeletonLoader height="2xl" rounded="none" />
          ) : (
            <p className={descriptionClassNames}>{description}</p>
          )}

          {isLoading ? (
            <SkeletonLoader height="xs" width="xs" rounded="none" />
          ) : (
            <p
              className={cn(
                roleplayCardIntroStyles.description,
                '!text-xs !font-normal text-neutral-gray-700',
              )}
            >
              {emCode}
            </p>
          )}
        </div>

        {isLoading ? (
          <div className={roleplayCardIntroStyles.avatarContainer}>
            <div className={roleplayCardIntroStyles.avatar}>
              <SkeletonLoader height="full" width="full" rounded="full" />
            </div>
            <SkeletonLoader className="h-[20px]" width="3/4" rounded="none" />
          </div>
        ) : (
          <>
            {(avatarName || avatarSrc) && (
              <div className={roleplayCardIntroStyles.avatarContainer}>
                <div className={roleplayCardIntroStyles.avatar}>
                  {avatarSrc && (
                    <AppImage
                      src={avatarSrc}
                      alt={avatarAlt || `${avatarName} avatar`}
                      className={roleplayCardIntroStyles.avatarImage}
                      fallbackSrc="/public/personas/fallback-persona.jpg"
                      fallbackClassName="translate-x-[-1px] translate-y-[-1px]"
                      height={64}
                      width={64}
                    />
                  )}
                </div>
                {avatarName && <p className={roleplayCardIntroStyles.avatarName}>{avatarName}</p>}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

RoleplayCardIntro.displayName = 'RoleplayCardIntro';

export default RoleplayCardIntro;
